import { type RefObject } from 'react';
import { Search, ArrowUp, ArrowDown, CornerDownLeft, ChevronRight } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle, Icon, Eyebrow } from '@/shared/ui';

export interface CommandItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  icon: React.ReactNode;
  shortcut?: string[];
  action: () => void;
}

interface CommandBarModalProps {
  onClose: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  filteredCommands: CommandItem[];
  groupedCommands: Record<string, CommandItem[]>;
  selectedIndex: number;
  onSelectCommand: (command: CommandItem) => void;
  onHoverCommand: (index: number) => void;
}

export const CommandBarModal = ({
  onClose,
  searchQuery,
  onSearchChange,
  inputRef,
  filteredCommands,
  groupedCommands,
  selectedIndex,
  onSelectCommand,
  onHoverCommand,
}: CommandBarModalProps) => (
  <Dialog
    open
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
  >
    <DialogContent
      // La palette s'ancre haut plutôt qu'au centre : c'est la convention de ce
      // geste, et le fond de fenêtre reste lisible pendant qu'on tape.
      className="bg-surface top-[15vh] w-[640px] max-w-[calc(100vw-2rem)] translate-y-0 gap-0 overflow-hidden p-0"
      onOpenAutoFocus={(event) => {
        // Le parent donne déjà le focus au champ de recherche ; laisser Radix
        // choisir le poserait sur le premier bouton de la liste.
        event.preventDefault();
      }}
    >
      {/* Le titre n'est pas dessiné — le champ de recherche tient ce rôle
          visuellement — mais il doit exister pour être annoncé. */}
      <DialogTitle className="sr-only">Palette de commandes</DialogTitle>
      {/* Search input */}
      <div className="border-border flex items-center gap-3 border-b px-4 py-3">
        <Icon icon={Search} size="lg" className="text-fg-subtle shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Saisir une commande ou rechercher…"
          value={searchQuery}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          aria-label="Recherche de commande"
          className="text-fg placeholder:text-fg-subtle flex-1 border-0 bg-transparent text-base outline-none"
        />
      </div>

      {/* Results */}
      <div className="max-h-[50vh] overflow-y-auto p-2">
        {filteredCommands.length === 0 ? (
          <div className="text-fg-muted px-3 py-8 text-center text-sm">Aucune commande trouvée</div>
        ) : (
          Object.entries(groupedCommands).map(([category, items]) => (
            <div key={category} className="mb-2 last:mb-0">
              <Eyebrow as="div" className="text-fg-subtle px-2 pt-2 pb-1">
                {category}
              </Eyebrow>
              <div className="flex flex-col gap-0.5">
                {items.map((command) => {
                  const globalIndex = filteredCommands.indexOf(command);
                  const isSelected = globalIndex === selectedIndex;
                  return (
                    <button
                      key={command.id}
                      type="button"
                      onClick={() => {
                        onSelectCommand(command);
                      }}
                      onMouseEnter={() => {
                        onHoverCommand(globalIndex);
                      }}
                      aria-label={command.title}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-md border-0 px-3 py-2 text-left transition-colors ${
                        // `bg-transparent` seulement hors sélection : posé en dur, il venait après
                        // `bg-primary` dans le CSS compilé et l'emportait — texte blanc sur blanc.
                        isSelected
                          ? 'bg-primary-strong text-on-primary'
                          : 'text-fg hover:bg-bg-muted bg-transparent'
                      }`}
                    >
                      <div
                        className={`flex size-8 shrink-0 items-center justify-center rounded-md ${
                          isSelected ? 'bg-white/20' : 'bg-bg-muted text-fg-muted'
                        }`}
                      >
                        {command.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{command.title}</div>
                        {command.description !== undefined && (
                          <div
                            className={`truncate text-xs ${isSelected ? 'text-white/80' : 'text-fg-muted'}`}
                          >
                            {command.description}
                          </div>
                        )}
                      </div>
                      {command.shortcut && (
                        <div className="flex shrink-0 items-center gap-0.5">
                          {command.shortcut.map((key, i) => (
                            <kbd
                              key={i}
                              className={`inline-flex h-5 min-w-5 items-center justify-center rounded-sm px-1 text-xs font-medium ${
                                isSelected
                                  ? 'border border-white/20 bg-white/10 text-white'
                                  : 'text-fg-muted border-border bg-bg-muted border'
                              }`}
                            >
                              {key}
                            </kbd>
                          ))}
                        </div>
                      )}
                      {isSelected && <Icon icon={ChevronRight} size="lg" className="shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-border bg-bg-subtle text-fg-muted flex items-center justify-between border-t px-4 py-2 text-sm">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="border-border bg-surface inline-flex size-4 items-center justify-center rounded-sm border">
              <Icon icon={ArrowUp} size="sm" />
            </kbd>
            <kbd className="border-border bg-surface inline-flex size-4 items-center justify-center rounded-sm border">
              <Icon icon={ArrowDown} size="sm" />
            </kbd>
            <span>Naviguer</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border-border bg-surface inline-flex size-4 items-center justify-center rounded-sm border">
              <Icon icon={CornerDownLeft} size="sm" />
            </kbd>
            <span>Sélectionner</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border-border bg-surface text-2xs inline-flex h-4 items-center justify-center rounded-sm border px-1 font-medium">
              Échap
            </kbd>
            <span>Fermer</span>
          </span>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
